import { Link } from 'react-router-dom';

export function OutboundSubscriptionsGuidePage() {
  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Guía de webhooks salientes</h1>
          <p className="page__subtitle">
            Cómo configurar quién recibe los eventos del middleware y revisar sus entregas.
          </p>
        </div>
        <Link className="button button--primary" to="/webhooks-salientes">
          Abrir Webhooks salientes
        </Link>
      </header>

      <section className="panel">
        <header className="panel__header">
          <h2>Antes de comenzar</h2>
        </header>
        <div className="panel__body">
          <p>
            Una suscripción conecta un evento con una URL receptora. Cuando el middleware publica
            ese evento, genera una entrega para cada suscripción activa que corresponda. Crear una
            suscripción no genera eventos ni envía los que ocurrieron antes.
          </p>
          <p>
            Todos los usuarios autorizados pueden consultar la lista y el historial. Para crear,
            editar, activar o desactivar necesitas el rol <strong>Administrador</strong>. Si no ves
            esos botones, solicita el acceso a quien administra el middleware.
          </p>
          <p>
            Acuerda con el responsable del sistema receptor el nombre exacto del evento, la URL y el
            secreto compartido. El receptor debe estar preparado para validar la autenticación y
            procesar el contenido recibido.
          </p>
        </div>
      </section>

      <section className="panel">
        <header className="panel__header">
          <h2>Crear una suscripción</h2>
        </header>
        <div className="panel__body">
          <ol>
            <li>
              Abre <Link to="/webhooks-salientes">Configuración → Webhooks salientes</Link> y pulsa{' '}
              <strong>Nueva suscripción</strong>.
            </li>
            <li>
              Ingresa el <strong>Evento</strong> tal como lo publica el middleware. Por ejemplo,{' '}
              <code>order.status.changed</code>; los puntos y guiones bajos no son intercambiables.
            </li>
            <li>
              Selecciona el <strong>Sistema destino</strong> e ingresa su{' '}
              <strong>URL de destino</strong>. Usa HTTPS, sin usuario, contraseña, parámetros ni
              fragmentos en la URL. HTTP sólo se admite en localhost para desarrollo.
            </li>
            <li>
              Ingresa el <strong>Secreto HMAC</strong> o la <strong>API key</strong> acordada con el
              receptor. No uses claves de ejemplo ni compartas el secreto en capturas.
            </li>
            <li>
              Guarda con <strong>Suscripción activa</strong> desmarcada para revisar la
              configuración. Las nuevas suscripciones se presentan inactivas.
            </li>
            <li>
              Cuando el receptor esté listo, pulsa <strong>Activar</strong>. Debe haber un secreto
              configurado. La activación no envía una prueba automáticamente.
            </li>
          </ol>
          <p>
            Guardar confirma la configuración; no confirma que el receptor haya recibido un evento.
            Después de una operación que publique ese evento, revisa <strong>Entregas</strong>.
          </p>
        </div>
      </section>

      <section className="panel">
        <header className="panel__header">
          <h2>Ejemplos para DecoraPOS</h2>
        </header>
        <div className="table__wrap">
          <table className="table">
            <caption>
              El dominio debe ser el del API de tu POS; las rutas indicadas son obligatorias.
            </caption>
            <thead>
              <tr>
                <th scope="col">Evento</th>
                <th scope="col">Ruta del receptor</th>
                <th scope="col">Autenticación</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>order.status.changed</code>
                </td>
                <td>
                  <code>/webhooks/pedidos</code>
                </td>
                <td>HMAC</td>
              </tr>
              <tr>
                <td>
                  <code>order.documents.updated</code>
                </td>
                <td>
                  <code>/webhooks/pedidos</code>
                </td>
                <td>HMAC</td>
              </tr>
              <tr>
                <td>
                  <code>order.canceled</code>
                </td>
                <td>
                  <code>/webhooks/pedidos</code>
                </td>
                <td>HMAC</td>
              </tr>
              <tr>
                <td>
                  <code>crm.customer.snapshot</code>
                </td>
                <td>
                  <code>/webhooks/clientes</code>
                </td>
                <td>API key</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="panel__body">
          <p>
            Selecciona <strong>DecoraPOS</strong> como destino y crea una suscripción por evento de
            pedidos que necesites recibir. Clientes usa la cabecera <code>x-amoble-api-key</code> y
            sólo admite una suscripción activa. Estos son ejemplos de eventos existentes, no un
            catálogo completo.
          </p>
        </div>
      </section>

      <section className="panel">
        <header className="panel__header">
          <h2>Editar o cambiar el secreto</h2>
        </header>
        <div className="panel__body">
          <p>
            Busca la suscripción por evento o URL; también puedes filtrar por sistema y estado.
            Pulsa <strong>Editar</strong> para cambiar URL, secreto o estado y luego
            <strong> Guardar suscripción</strong>.
          </p>
          <p>
            El secreto guardado nunca se muestra. Dejar el campo vacío conserva el valor anterior;
            escribir uno nuevo lo reemplaza. Coordina el cambio con el receptor: URL y secreto
            nuevos también se usarán en entregas que aún estén pendientes.
          </p>
          <p>
            Evento y sistema quedan fijos después del alta. Si necesitas cambiarlos, crea otra
            suscripción y desactiva la anterior. No se permite duplicar la combinación de evento,
            sistema y URL; busca primero si ya existe una suscripción inactiva para reutilizarla.
          </p>
        </div>
      </section>

      <section className="panel">
        <header className="panel__header">
          <h2>Desactivar y reactivar</h2>
        </header>
        <div className="panel__body">
          <p>
            <strong>Desactivar</strong> detiene nuevos envíos y conserva la suscripción y su
            historial. No hay una acción de eliminación. Las solicitudes que ya salieron no se
            pueden retirar.
          </p>
          <p>
            Una entrega pendiente del procesador general se marca como fallida si encuentra la
            suscripción desactivada; clientes sigue su propia política de reintentos. Al
            <strong> Activar</strong> otra vez no se reproducen automáticamente los eventos
            históricos. Si falta el secreto o la URL antigua no cumple los requisitos, corrige la
            configuración antes de reactivar.
          </p>
        </div>
      </section>

      <section className="panel">
        <header className="panel__header">
          <h2>Revisar entregas y resolver problemas</h2>
        </header>
        <div className="panel__body">
          <p>
            Pulsa <strong>Entregas</strong> en la fila de una suscripción. El historial muestra la
            fecha, el estado, los intentos y el último error. Usa <strong>Anterior</strong> y
            <strong> Siguiente</strong> para recorrer las páginas, o{' '}
            <strong>Actualizar entregas</strong> para releer el estado.
          </p>
          <ul>
            <li>
              <strong>Pendiente:</strong> todavía no hay una entrega confirmada. Actualiza el
              historial para seguir su avance.
            </li>
            <li>
              <strong>Entregada:</strong> el middleware recibió la respuesta esperada del receptor.
              Esto no demuestra por sí solo que todos los procesos posteriores del sistema destino
              hayan terminado.
            </li>
            <li>
              <strong>Fallida:</strong> revisa el error y los intentos. Abre{' '}
              <strong>Ver detalle</strong> para consultar el mensaje y su trazabilidad; este estado
              por sí solo no indica que se agotaron los reintentos.
            </li>
          </ul>
          <p>
            Si no aparecen entregas, comprueba que la suscripción esté activa, que el evento
            coincida exactamente y que una operación posterior al alta lo haya publicado. No todos
            los cambios de negocio publican todos los eventos.
          </p>
          <p>
            Si se rechaza el guardado, revisa URL, secreto y posibles duplicados. Para clientes,
            comprueba además que no exista otro destino activo. Si el envío falla, usa el error
            registrado para revisar la disponibilidad y autenticación del receptor.
          </p>
          <p>
            El historial de esta pantalla corresponde a entregas. Para saber quién cambió una
            configuración, solicita la revisión de los logs de administración; allí queda registrado
            el actor sin exponer el secreto.
          </p>
        </div>
      </section>
    </div>
  );
}
